// src/views/words-list/useWordsCatalog.ts
import {
  MutableRefObject, useCallback, useEffect, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { message } from 'antd';
import API, { CatalogCandidate } from '../../services/ApiService';
import { error, success } from '../../components/Message';
import { getCanCurate } from '../auth/slice/selectors';
import { fetchMe } from '../auth/slice/actions';
import {
  catalogErrorCode, catalogErrorKey, chunkForLookup, isCatalogTerm,
} from '../../utils/catalog';

type CatalogPair = { term: string; assetId: number };

// Para w katalogu to zwrot + obrazek. Zwrot jest tu surowy — tak, jak poszedł do `lookup`;
// normalizuje backend, więc po zmianie hasła w słowie stan pary jest po prostu nieznany.
const catalogPairKey = (term: string, assetId: number): string => `${assetId}|${term}`;

// Słowa, które da się uzupełnić z katalogu. Backend nie wie, które mają już obrazek — pytamy
// tylko o te bez niego, z hasłem, o które w ogóle da się zapytać.
export const getWordsToFill = (formWords: any[] = []): Array<{ id: string; term: string }> => formWords
  .filter((word) => !word.image && isCatalogTerm(word.term))
  .map((word) => ({ id: word.id, term: word.term }));

interface UseWordsCatalogParams {
  isDataSynced: boolean;
  wordsList: { words?: any[] } | null;
  formikRef: MutableRefObject<FormikProps<any> | null>;
  setWords: (words: any[]) => void;
}

// Wszystko, co edytor listy robi z katalogiem wzorcowych obrazków. Katalog nie zna list, a zapis
// listy nie przechodzi przez żaden kod katalogu — tu tylko czytamy z katalogu i dodajemy do niego
// pojedyncze pary, więc edytor i jego autozapis zostają takie, jak przed katalogiem.
export const useWordsCatalog = ({
  isDataSynced, wordsList, formikRef, setWords,
}: UseWordsCatalogParams) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const canCurate = useSelector(getCanCurate);
  const [isFillingFromCatalog, setIsFillingFromCatalog] = useState(false);
  // Pary, o których wiemy, że są w katalogu (klucze z `catalogPairKey`)
  const [catalogPairs, setCatalogPairs] = useState<Set<string>>(new Set());
  const [addingToCatalogId, setAddingToCatalogId] = useState<string | null>(null);

  const markInCatalog = useCallback((pairs: CatalogPair[]) => {
    setCatalogPairs((current) => {
      const next = new Set(current);
      pairs.forEach(({ term, assetId }) => next.add(catalogPairKey(term, assetId)));

      return next;
    });
  }, []);

  const isInCatalog = useCallback((word: any): boolean => Boolean(
    word?.imageAssetId && catalogPairs.has(catalogPairKey(word.term, word.imageAssetId)),
  ), [catalogPairs]);

  // Które pary z tej listy są już w katalogu — tylko u kuratora, po wczytaniu i po każdym zapisie.
  // `lookup` zwraca wyłącznie aktywne wpisy z naszymi obrazkami, więc to wystarcza do „✓ W katalogu".
  useEffect(() => {
    if (!canCurate || !isDataSynced || !wordsList) {
      return undefined;
    }

    const terms: string[] = Array.from(new Set((wordsList.words || [])
      .filter((word: any) => word.image && word.imageAssetId && isCatalogTerm(word.term))
      .map((word: any) => word.term)));

    if (!terms.length) {
      return undefined;
    }

    let isCurrent = true;
    const chunks = chunkForLookup(terms);

    Promise.all(chunks.map((chunk) => API.catalog.lookup(chunk)))
      .then((responses) => {
        if (!isCurrent) {
          return;
        }

        const pairs: CatalogPair[] = [];
        responses.forEach(({ data }, chunkIndex) => {
          data.results.forEach(({ candidates }, index) => {
            candidates.forEach(({ assetId }) => pairs.push({ term: chunks[chunkIndex][index], assetId }));
          });
        });
        markInCatalog(pairs);
      })
      // Bez odpowiedzi przyciski zostają „Do katalogu" — kliknięcie przy parze, która już jest, to wyjaśni
      .catch((e) => console.error('❌ Catalog lookup failed:', e));

    return () => {
      isCurrent = false;
    };
  }, [canCurate, isDataSynced, wordsList, markInCatalog]);

  // Dodanie jednej pary zwrot → obrazek. Lista nie musi być zapisana — backend patrzy tylko
  // na obrazek, a ten istnieje od chwili uploadu. Po dodaniu wpis żyje niezależnie od listy.
  const addToCatalog = useCallback(async (word: any) => {
    setAddingToCatalogId(word.id);

    try {
      const { status } = await API.catalog.add(word.term, word.imageAssetId);

      markInCatalog([{ term: word.term, assetId: word.imageAssetId }]);
      success(t(status === 200 ? 'words-list:catalog.messages.restored' : 'words-list:catalog.messages.added'));
    } catch (e) {
      const code = catalogErrorCode(e);

      // Para już jest — to nie błąd, tylko przycisk nie wiedział o tym wcześniej
      if (code === 'CATALOG_ENTRY_EXISTS') {
        markInCatalog([{ term: word.term, assetId: word.imageAssetId }]);
        message.info(t(catalogErrorKey(e)));

        return;
      }

      error(t(catalogErrorKey(e)));

      // Uprawnienia zdjęto w trakcie sesji — odświeżony profil schowa przyciski
      if (code === 'CATALOG_NOT_CURATOR') {
        dispatch(fetchMe());
      }
    } finally {
      setAddingToCatalogId(null);
    }
  }, [dispatch, markInCatalog, t]);

  // Masowe podstawienie z katalogu. `results[i]` odpowiada `terms[i]`, więc słowo trzymamy po pozycji,
  // nie po haśle — dwa „give up" na jednej liście to norma. Niczego nie zapisuje, działa jak wybór
  // z szuflady: zmiany zapisze autozapis albo przycisk.
  const fillFromCatalog = useCallback(async () => {
    if (!formikRef.current || isFillingFromCatalog) {
      return;
    }

    const pending = getWordsToFill(formikRef.current.values.words);

    if (!pending.length) {
      return;
    }

    setIsFillingFromCatalog(true);

    try {
      const chunks = chunkForLookup(pending);
      const responses = await Promise.all(
        chunks.map((chunk) => API.catalog.lookup(chunk.map(({ term }) => term))),
      );

      const picks = new Map<string, { term: string; candidate: CatalogCandidate }>();
      responses.forEach(({ data }, chunkIndex) => {
        data.results.forEach(({ candidates: [first] }, index) => {
          const word = chunks[chunkIndex][index];

          if (first && word) {
            picks.set(word.id, { term: word.term, candidate: first });
          }
        });
      });

      // Stan formularza z chwili odpowiedzi, nie kliknięcia: w międzyczasie nauczyciel mógł wybrać
      // obrazek z szuflady albo zmienić hasło — tego nie nadpisujemy
      const filledPairs: CatalogPair[] = [];
      const newWords = (formikRef.current?.values.words || []).map((word: any) => {
        const pick = picks.get(word.id);

        if (!pick || word.image || word.term !== pick.term) {
          return word;
        }

        filledPairs.push({ term: pick.term, assetId: pick.candidate.assetId });

        // `imageMeta` z katalogu wkładamy bez zmian — nieznane pole dałoby 400 przy autozapisie
        return {
          ...word,
          image: pick.candidate.url,
          imageMeta: pick.candidate.imageMeta,
          imageAssetId: pick.candidate.assetId,
        };
      });

      if (filledPairs.length) {
        formikRef.current?.setFieldValue('words', newWords);
        setWords(newWords);
        markInCatalog(filledPairs);
        success(t('words-list:catalog.fill.success', { filled: filledPairs.length, total: pending.length }));
      } else {
        message.info(t('words-list:catalog.fill.empty'));
      }
    } catch (e) {
      console.error('❌ Catalog lookup failed:', e);
      error(t('words-list:catalog.fill.error'));
    } finally {
      setIsFillingFromCatalog(false);
    }
  }, [formikRef, isFillingFromCatalog, markInCatalog, setWords, t]);

  return {
    canCurate,
    isInCatalog,
    markInCatalog,
    addToCatalog,
    addingToCatalogId,
    fillFromCatalog,
    isFillingFromCatalog,
  };
};

export default useWordsCatalog;
